import type { FC } from 'react'
import { useState } from 'react'

import { MessagePopup, Table } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Td } from '@skytech/ui/Table/Td'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import CheckDecagramIcon from 'mdi-react/CheckDecagramIcon'

import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { LocalDateTime } from '@/components/LocalDateTime'
import Address from '@/components/primitives/Address'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'

import { queries } from './queries'
import { shopOrderResources } from './resources'

const EcomOrderDetails: FC<{
  id: number
  customer_name: string
  org_no: string
  billing_address1?: string
  billing_zip?: string
  billing_city?: string
  vendor_name: string
  order_date: string
  quote_name: string
  totalCost: number
  approved_at?: string
}> = ({
  customer_name,
  org_no,
  billing_address1,
  billing_zip,
  billing_city,
  vendor_name,
  order_date,
  approved_at,
  quote_name,
  totalCost = 0,
  id: orderId,
}) => {
  const { whiteLabel } = useAppContext()
  const [isAuditLogPopupOpen, setIsAuditLogPopupOpen] = useState(false)

  const { data: auditLog } = useQuery(
    queries.auditLog(orderId),
    () => shopOrderResources.getAuditLog(orderId),
    { enabled: Boolean(orderId) }
  )

  const lastUpdate = auditLog?.[0]
  const updatedDatetime = lastUpdate?.timestamp
  const updatedBy = lastUpdate && `${lastUpdate?.user?.firstName} ${lastUpdate?.user?.lastName}`

  return (
    <section>
      <h3>
        <Translation id="Details" />
      </h3>

      <DataPoints>
        <DataPoint header={<Translation id="Customer" />} value={customer_name} />

        <DataPoint header={<Translation id="Organization Number" />} value={org_no} />
        <DataPoint
          header={<Translation id="Billing Address" />}
          // @ts-ignore
          value={
            !billing_address1 && !billing_zip && !billing_city ? (
              '-'
            ) : (
              <Address street1={billing_address1} zip={billing_zip} city={billing_city} />
            )
          }
        />

        <DataPoint header={<Translation id="Vendor" />} value={vendor_name} />

        <DataPoint
          header={<Translation id="Order date" />}
          value={order_date ? <LocalDate date={order_date} /> : '-'}
        />

        {approved_at && (
          <DataPoint
            header={<Translation id="Approval date" />}
            value={
              <>
                <LocalDateTime date={approved_at} />
                {/* todo: Remove ! when refactored */}
                <CheckDecagramIcon className="ml-1 !h-6 !w-6 fill-green-500" />
              </>
            }
          />
        )}

        <DataPoint
          header={<Translation id="Cost" />}
          value={<FormattedNumberWithCurrency amount={totalCost} />}
        />

        {quote_name && <DataPoint header={<Translation id="Quote name" />} value={quote_name} />}

        {updatedDatetime && (
          <DataPoint
            header={<Translation id="Last updated at" />}
            value={<LocalDateTime date={updatedDatetime} />}
          />
        )}

        {updatedBy && <DataPoint header={<Translation id="Last updated by" />} value={updatedBy} />}

        {auditLog && auditLog.length > 0 && (
          <div className="mx-2 flex-[1_1_15rem]">
            <Button
              className="m-0 h-auto p-0"
              variant="tertiary"
              onClick={() => setIsAuditLogPopupOpen(true)}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            >
              <>
                (
                <Translation id="History" />)
              </>
            </Button>
          </div>
        )}
      </DataPoints>

      <MessagePopup
        variant="wide"
        type="error"
        title={<Translation id="Activity history" />}
        isOpen={isAuditLogPopupOpen}
        onClose={() => setIsAuditLogPopupOpen(false)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <div className="max-h-[40%] overflow-auto">
          <Table rowHeight="medium">
            <thead>
              <tr>
                <th>
                  <Translation id="Date" />
                </th>

                <th>
                  <Translation id="User" />
                </th>

                <th>
                  <Translation id="Modification type" />
                </th>
              </tr>
            </thead>
            <tbody>
              {auditLog?.map(record => (
                <tr key={record.timestamp}>
                  <Td>
                    <LocalDateTime date={record.timestamp} />
                  </Td>
                  <Td>
                    {record.user?.username} ({record.user?.firstName} {record.user?.lastName})
                  </Td>
                  <Td>
                    <Translation id={`AUDIT_LOG_ACTION/${record.action}`} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </MessagePopup>
    </section>
  )
}

export default EcomOrderDetails
